import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Spinner,
} from 'react-bootstrap';

import {
  jtaService, JtaContent as JtaContentModel, JtaStat,
} from '../../../components/Jta/services/jtaService';
import { showError, showSuccess } from '../../../Utilities/sweetAlert';

const JtaContent: React.FC = () => {
  const [content, setContent] = useState<JtaContentModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await jtaService.getContent();
      setContent(response.data.content);
    } catch (err) {
      showError('Failed to load JTA content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = <K extends keyof JtaContentModel>(key: K, value: JtaContentModel[K]) => {
    setContent((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const setStat = (index: number, key: keyof JtaStat, value: string) => {
    if (!content) return;
    const stats = content.stats.map((s, i) => (i === index ? { ...s, [key]: value } : s));
    setField('stats', stats);
  };

  const addStat = () => content && setField('stats', [...content.stats, { value: '', label: '' }]);
  const removeStat = (index: number) =>
    content && setField('stats', content.stats.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const { id, ...payload } = content;
      const response = await jtaService.updateContent(payload);
      setContent(response.data.content);
      showSuccess('JTA content updated successfully!');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save JTA content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" /></div>;
  }

  if (!content) {
    return <Container className="py-4"><p className="text-muted">No content available.</p></Container>;
  }

  return (
    <Container className="py-4">
      <Row className="align-items-center mb-4">
        <Col>
          <h2 className="mb-0">JTA Landing Content</h2>
          <small className="text-muted">Edit the copy and links shown on the app&apos;s Jordan Trail page. Hero images are managed in the app.</small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save changes'}
          </Button>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>Trail identity</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Identity label</Form.Label>
            <Form.Control value={content.identityLabel} onChange={(e) => setField('identityLabel', e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control value={content.title} onChange={(e) => setField('title', e.target.value)} />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>About the Trail</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Section title</Form.Label>
            <Form.Control value={content.aboutTitle} onChange={(e) => setField('aboutTitle', e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Body</Form.Label>
            <Form.Control as="textarea" rows={5} value={content.aboutBody} onChange={(e) => setField('aboutBody', e.target.value)} />
            <Form.Text className="text-muted">Separate paragraphs with a blank line.</Form.Text>
          </Form.Group>
          <Form.Label>Stats</Form.Label>
          {content.stats.map((stat, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={index} className="g-2 mb-2 align-items-center">
              <Col><Form.Control placeholder="Value (e.g. 650)" value={stat.value} onChange={(e) => setStat(index, 'value', e.target.value)} /></Col>
              <Col><Form.Control placeholder="Label (e.g. km total)" value={stat.label} onChange={(e) => setStat(index, 'label', e.target.value)} /></Col>
              <Col xs="auto"><Button variant="outline-danger" size="sm" onClick={() => removeStat(index)}>Remove</Button></Col>
            </Row>
          ))}
          <Button variant="outline-secondary" size="sm" onClick={addStat}>+ Add stat</Button>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>Jordan Trail Association</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Section title</Form.Label>
            <Form.Control value={content.orgTitle} onChange={(e) => setField('orgTitle', e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Body</Form.Label>
            <Form.Control as="textarea" rows={5} value={content.orgBody} onChange={(e) => setField('orgBody', e.target.value)} />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Website URL</Form.Label>
                <Form.Control value={content.websiteUrl} onChange={(e) => setField('websiteUrl', e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Instagram URL</Form.Label>
                <Form.Control value={content.instagramUrl} onChange={(e) => setField('instagramUrl', e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>Call to action</Card.Header>
        <Card.Body>
          <Form.Group>
            <Form.Label>Button label</Form.Label>
            <Form.Control value={content.ctaLabel} onChange={(e) => setField('ctaLabel', e.target.value)} />
          </Form.Group>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default JtaContent;
